import React, { useEffect, useRef, useImperativeHandle, useMemo, useState } from "react";
import {
    Button,
    Box,
    Container,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from "@mui/material";
import { useTheme, ThemeProvider } from '@mui/material/styles';
import { styled } from '@mui/system';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PropTypes from 'prop-types';
import { mockMessages } from './globalCons.js';
import getAvatarDisplayUrl from './promptEditorUtils/avatarUtils.js';

// =============================================
// STYLED COMPONENTS
// =============================================
const UserMessage = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '12px 26px 12px 22px',
    borderRadius: '16px 16px 0 16px',
    maxWidth: '70%',
    fontSize: '0.95rem',
    lineHeight: 1.4,
    transition: 'all 0.3s ease',
    margin: '8px 0',
    position: 'relative',
}));

const MessageWrapper = styled('div')(({ theme, align }) => ({
    display: 'flex',
    marginBottom: theme.spacing(1),
    justifyContent: align === 'user' ? 'flex-end' : 'flex-start',
}));

const AgentMessage = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[700],
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.white,
    padding: '12px 26px 12px 22px',
    borderRadius: '16px 16px 16px 0',
    maxWidth: '70%',
    fontSize: '0.95rem',
    lineHeight: 1.4,
    transition: 'all 0.3s ease',
    margin: '8px 0',
    position: 'relative',
}));


// =============================================
// CONVERSATION HISTORY DIALOG COMPONENT
// =============================================
const ConversationHistoryDialog = React.memo(({ showHistory, setShowHistory, conversationHistory }) => {
    return (
        <Dialog open={showHistory} onClose={() => setShowHistory(false)} maxWidth="md" fullWidth>
            <DialogTitle>
                Conversation History ({conversationHistory.length} messages)
            </DialogTitle>
            <DialogContent>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {conversationHistory.length === 0 ? (
                        <Typography>No conversation history found.</Typography>
                    ) : (
                        conversationHistory.map((conv, index) => (
                            <Box key={index} sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                <Typography variant="subtitle2" color="primary">
                                    User:
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {conv.user}
                                </Typography>
                                <Typography variant="subtitle2" color="secondary">
                                    Assistant:
                                </Typography>
                                <Typography variant="body2">
                                    {conv.assistant}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowHistory(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    );
});


ConversationHistoryDialog.propTypes = {
    showHistory: PropTypes.bool.isRequired,
    setShowHistory: PropTypes.func.isRequired,
    conversationHistory: PropTypes.array.isRequired,
};


// =============================================
// CHAT MESSAGES DISPLAY COMPONENT
// =============================================
const ChatMessages = React.memo(React.forwardRef(({ messages, playAudio, generateTTSAudio, setMessages, selectedChatbot, chatbotConfigs }, ref) => {
    const theme = useTheme();
    const bottomRef = useRef(null);
    const [loadingAudioIndex, setLoadingAudioIndex] = useState(null);

    // Force browser refresh when chatbot/avatar changes so replaced files are shown immediately.
    const selectedAvatarDisplayUrl = useMemo(() => {
        const rawAvatarUrl = chatbotConfigs[selectedChatbot]?.avatar_url;
        const normalizedAvatarUrl = getAvatarDisplayUrl(rawAvatarUrl);
        if (!normalizedAvatarUrl) {
            return '';
        }

        const [basePath, queryString = ''] = normalizedAvatarUrl.split('?');
        const queryParams = new URLSearchParams(queryString);
        queryParams.set('cb', `${selectedChatbot || 'chatbot'}_${Date.now()}`);

        return `${basePath}?${queryParams.toString()}`;
    }, [selectedChatbot, chatbotConfigs]);

    useEffect(() => {
        // This prevents scrolling when `messages` is just `mockMessages` on initial load.
        if (messages.length > 1 || (messages.length === 1 && messages[0] !== mockMessages[0])) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Expose the internal scrollToBottom function via the ref
    useImperativeHandle(ref, () => ({
        scrollToBottom: () => {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }));


    const handlePlayAudio = async (message, index) => {
        if (!message.audioUrl) {
            setLoadingAudioIndex(index);
            try {
                const audioUrl = await generateTTSAudio(message.text);
                if (audioUrl) {
                    setMessages(prevMessages => {
                        const updatedMessages = [...prevMessages];
                        updatedMessages[index] = {
                            ...updatedMessages[index],
                            audioUrl: audioUrl
                        };
                        return updatedMessages;
                    });
                    playAudio(audioUrl);
                }
            } finally {
                setLoadingAudioIndex(null);
            }
        } else {
            playAudio(message.audioUrl);
        }
    };

    return (
        <Container>
            {/* Instructions TextBox*/}
            {chatbotConfigs[selectedChatbot]?.instructions && chatbotConfigs[selectedChatbot].instructions.trim() !== '' && (
                <TextField
                    label="Instructions for the Students"
                    value={chatbotConfigs[selectedChatbot].instructions}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    InputProps={{
                        readOnly: true,
                        tabIndex: -1
                    }}
                    sx={{
                        '& .MuiInputBase-root': {
                            overflowY: 'auto',
                            caretColor: 'transparent',
                            userSelect: 'none',
                        },
                        '& .MuiInputBase-input': {
                            caretColor: 'transparent',
                            userSelect: 'none',
                        },
                    }}
                />
            )}
            <Box sx={{
                width: '100%',
                mt: 4,
                maxHeight: 300,
                minHeight: 300,
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: (theme) => `${theme.palette.primary.main} transparent`,
                '&::-webkit-scrollbar': {
                    width: '8px',
                    borderRadius: '16px',
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: (theme) => theme.palette.primary.main,
                    borderRadius: '16px',
                    border: '2px solid transparent',
                    backgroundClip: 'padding-box',
                    transition: 'background-color 0.3s ease',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: (theme) => theme.palette.primary.dark,
                },
            }}>
                <Paper elevation={0} sx={{
                    padding: 2,
                    backgroundColor: 'transparent',
                    borderRadius: 2,
                    boxShadow: 'none'
                }}>
                    <List>
                        {messages.map((message, index) => (
                            <ListItem key={index} sx={{ padding: 0 }}>
                                <ListItemText
                                    sx={{ margin: 0 }}
                                    primary={
                                        <MessageWrapper align={message.role}>
                                            {message.role === 'user' ? (
                                                <UserMessage theme={theme}>
                                                    {message.text}
                                                    {message.audioUrl && (
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                right: 8,
                                                                transform: 'translateY(-50%)',
                                                                color: 'inherit',
                                                                opacity: 0.8,
                                                                '&:hover': { opacity: 1 }
                                                            }}
                                                            onClick={() => playAudio(message.audioUrl)}
                                                        >
                                                            <VolumeUpIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </UserMessage>
                                            ) : (
                                                <>
                                                    {/* Chatbot avatar */}
                                                    {chatbotConfigs[selectedChatbot]?.avatar_url && (
                                                        <Box sx={{
                                                            width: 50,
                                                            height: 50,
                                                            minWidth: 40,
                                                            minHeight: 40,
                                                            borderRadius: '50%',
                                                            overflow: 'hidden',
                                                            backgroundColor: theme.palette.background.paper,
                                                            boxShadow: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            mr: 1.5
                                                        }}>
                                                            <img
                                                                src={selectedAvatarDisplayUrl}
                                                                alt="avatar"
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        </Box>
                                                    )}
                                                    <AgentMessage theme={theme}>

                                                        {message.text}
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                right: 8,
                                                                transform: 'translateY(-50%)',
                                                                color: 'inherit',
                                                                opacity: 0.8,
                                                                '&:hover': { opacity: 1 }
                                                            }}
                                                            onClick={() => handlePlayAudio(message, index)}
                                                            disabled={loadingAudioIndex === index}
                                                        >
                                                            {loadingAudioIndex === index ? (
                                                                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                                            ) : (
                                                                <VolumeUpIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </AgentMessage>
                                                </>
                                            )}
                                        </MessageWrapper>
                                    }
                                />
                            </ListItem>
                        ))}
                        <div ref={bottomRef} />
                    </List>
                </Paper>
            </Box>
        </Container>
    );
}))

ChatMessages.propTypes = {
    playAudio: PropTypes.func.isRequired,
    messages: PropTypes.array.isRequired,
    generateTTSAudio: PropTypes.func.isRequired,
    setMessages: PropTypes.func.isRequired,
    selectedChatbot: PropTypes.string,
    chatbotConfigs: PropTypes.object,
};


export { AgentMessage, UserMessage, MessageWrapper, ConversationHistoryDialog, ChatMessages };